/**
 * Access predicates shared by the App.js route guards, the navigation, the command palette
 * and the widget dashboard, so a permission or feature-flag rule is written once.
 *
 * Permissions arrive from `/api/users/me/` as `permissions` on each organization entry: the
 * effective keys of organizations/permission_registry.py for the user's role, including the
 * organization's own overrides. Feature flags are flat fields on the same object.
 */

/** True when the current organization grants `key` (a registry key such as 'invoices.view'). */
export function can(currentOrganization, key) {
  if (!key) return true;
  const list = currentOrganization?.permissions;
  return Array.isArray(list) && list.includes(key);
}

/** True while an organization entry has no `permissions` list yet (backend not upgraded, or a stale payload). */
export function permissionsPending(currentOrganization) {
  return Boolean(currentOrganization) && !Array.isArray(currentOrganization.permissions);
}

/**
 * The simplified "own work" UI (work queue, five-item menu, own calendar row) is for users who
 * see work orders but only their own. Derived from the scope key, never a key of its own.
 */
export function isSimplifiedUi(currentOrganization) {
  return can(currentOrganization, 'work_orders.view') && !can(currentOrganization, 'work_orders.view_all');
}

/** The calendar shows only the user's own row (first visit, resource switcher, dashboard widgets). */
export function ownCalendarOnly(currentOrganization) {
  return !can(currentOrganization, 'calendar.view_all');
}

/** Fail-open: a flag missing from the payload counts as enabled (FeatureProtectedRoute). */
export function featureEnabled(currentOrganization, featureKey) {
  if (!featureKey) return true;
  return Boolean(currentOrganization?.[featureKey] ?? true);
}

/** Fail-closed: opt-in features stay hidden until the flag is explicitly true (StrictFeatureProtectedRoute). */
export function strictFeatureEnabled(currentOrganization, featureKey) {
  if (!featureKey) return true;
  return currentOrganization?.[featureKey] === true;
}
